<template>
  <div class="file-transfer-demo">
    <!-- File Transfer Interface -->
    <div class="bg-white rounded-lg border border-gray-200">
      <!-- Header -->
      <div class="bg-green-600 text-white p-3 rounded-t-lg flex items-center justify-between">
        <div class="flex items-center gap-2">
          <IconifyIcon icon="heroicons:document-arrow-down-20-solid" class="w-5 h-5" />
          <span class="font-medium">File Transfer Demo</span> <span class="ml-1 text-xs font-normal opacity-80">(simulated, no real connection)</span>
        </div>
        <div class="flex items-center gap-1">
          <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span class="text-xs">Ready</span>
        </div>
      </div>

      <!-- Drop Zone -->
      <div
        @drop="handleDrop"
        @dragover.prevent
        @dragenter.prevent
        class="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-green-400 transition-colors cursor-pointer"
        :class="{ 'bg-green-50': isDragging }"
        @dragenter="isDragging = true"
        @dragleave="isDragging = false"
      >
        <IconifyIcon icon="heroicons:cloud-arrow-up-20-solid" class="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p class="text-gray-600 font-medium">Drop files here or click to browse</p>
        <p class="text-sm text-gray-500 mt-1">Max file size: 10MB</p>
        <input
          ref="fileInput"
          type="file"
          multiple
          @change="handleFileSelect"
          class="hidden"
        />
        <button
          @click="(fileInput as HTMLInputElement).click()"
          class="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
        >
          Select Files
        </button>
      </div>

      <!-- File List -->
      <div v-if="files.length > 0" class="p-4 border-t border-gray-200">
        <h5 class="font-medium text-gray-900 mb-3">Files to Transfer</h5>
        <div class="space-y-2">
          <div
            v-for="file in files"
            :key="file.id"
            class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
          >
            <IconifyIcon icon="heroicons:document-20-solid" class="w-5 h-5 text-gray-600" />
            <div class="flex-1">
              <p class="text-sm font-medium text-gray-900">{{ file.name }}</p>
              <p class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
            </div>
            <div class="flex items-center gap-2">
              <!-- Progress bar -->
              <div v-if="file.progress > 0" class="w-20 bg-gray-200 rounded-full h-2">
                <div
                  class="bg-green-600 h-2 rounded-full transition-all duration-300"
                  :style="{ width: `${file.progress}%` }"
                ></div>
              </div>
              <!-- Status -->
              <span class="text-xs" :class="getStatusClass(file.status)">
                {{ file.status }}
              </span>
              <!-- Remove button -->
              <button
                @click="removeFile(file.id)"
                class="text-red-500 hover:text-red-700 transition-colors"
              >
                <IconifyIcon icon="heroicons:x-mark-20-solid" class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Transfer Controls -->
        <div class="mt-4 flex gap-2">
          <button
            @click="startTransfer"
            :disabled="files.length === 0 || isTransferring"
            class="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <IconifyIcon v-if="isTransferring" icon="heroicons:arrow-path-20-solid" class="w-4 h-4 animate-spin" />
            <IconifyIcon v-else icon="heroicons:paper-airplane-20-solid" class="w-4 h-4" />
            {{ isTransferring ? 'Transferring...' : 'Start Transfer' }}
          </button>
          <button
            @click="clearFiles"
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import IconifyIcon from '../../IconifyIcon.vue'

interface FileItem {
  id: number
  name: string
  size: number
  status: 'pending' | 'transferring' | 'completed' | 'error'
  progress: number
}

// Reactive state
const files = ref<FileItem[]>([])
const isDragging = ref(false)
const isTransferring = ref(false)
const fileInput = ref<HTMLInputElement>()
let nextFileId = 1

// Methods
const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files) {
    addFiles(Array.from(target.files))
  }
}

const handleDrop = (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = false

  if (event.dataTransfer?.files) {
    addFiles(Array.from(event.dataTransfer.files))
  }
}

const addFiles = (newFiles: File[]) => {
  newFiles.forEach(file => {
    files.value.push({
      id: nextFileId++,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0
    })
  })
}

const removeFile = (id: number) => {
  files.value = files.value.filter(file => file.id !== id)
}

const clearFiles = () => {
  files.value = []
}

const startTransfer = async () => {
  if (files.value.length === 0) return

  isTransferring.value = true

  // Simulate file transfer
  for (const file of files.value) {
    file.status = 'transferring'
    file.progress = 0

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      file.progress += Math.random() * 20
      if (file.progress >= 100) {
        file.progress = 100
        file.status = 'completed'
        clearInterval(progressInterval)
      }
    }, 300)

    await new Promise(resolve => setTimeout(resolve, 500))
  }

  isTransferring.value = false
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const getStatusClass = (status: string): string => {
  switch (status) {
    case 'transferring':
      return 'text-blue-600 font-medium'
    case 'completed':
      return 'text-green-600 font-medium'
    case 'error':
      return 'text-red-600 font-medium'
    default:
      return 'text-gray-500'
  }
}
</script>

<style scoped>
.file-transfer-demo {
  /* Component-specific styles */
}
</style>